import Phaser from 'phaser';
import { TEXTURE_KEYS } from '../assets/assetKeys';
import { ScoreSystem } from '../systems/ScoreSystem';
import { Player } from './Player';

export type HazardType = 'falling_tree' | 'ground_crack' | 'flood' | 'landslide' | 'debris';

const HAZARD_TEXTURE_MAP: Partial<Record<HazardType, string>> = {
  falling_tree: TEXTURE_KEYS.TREE_FALLEN,
  ground_crack: TEXTURE_KEYS.GROUND_CRACK,
  debris: TEXTURE_KEYS.TREE_FALLEN,
};

export interface HazardConfig {
  id: string;
  type: HazardType;
  pointPenalty?: number;
  cooldownMs?: number;
  onHit?: (player: Player, score: ScoreSystem) => void;
}

export class Hazard extends Phaser.Physics.Arcade.Sprite {
  readonly hazardId: string;
  readonly hazardType: HazardType;
  readonly pointPenalty: number;
  private hazardActive = true;
  private cooldownMs: number;
  private lastHitTime = 0;
  private onHitCallback?: (player: Player, score: ScoreSystem) => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    config: HazardConfig,
  ) {
    const texture = HAZARD_TEXTURE_MAP[config.type] ?? TEXTURE_KEYS.GROUND_CRACK;
    super(scene, x, y, texture);

    this.hazardId = config.id;
    this.hazardType = config.type;
    this.pointPenalty = config.pointPenalty ?? 10;
    this.cooldownMs = config.cooldownMs ?? 1500;
    this.onHitCallback = config.onHit;

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.setDepth(6);
    const frame = scene.textures.getFrame(texture);
    this.body?.setSize(frame.width * 0.85, frame.height * 0.55);
    this.body?.setOffset(frame.width * 0.075, frame.height * 0.35);
  }

  onPlayerOverlap(player: Player, score: ScoreSystem): void {
    if (!this.hazardActive) return;
    const now = this.scene.time.now;
    if (now - this.lastHitTime < this.cooldownMs) return;
    this.lastHitTime = now;

    if (this.onHitCallback) {
      this.onHitCallback(player, score);
    } else {
      this.flashWarning();
    }
  }

  private flashWarning(): void {
    const cam = this.scene.cameras.main;
    cam.flash(200, 200, 50, 50);
  }

  deactivate(): void {
    this.hazardActive = false;
    this.setVisible(false);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.enable = false;
  }

  isActive(): boolean {
    return this.hazardActive;
  }
}
